import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { Mail, Send, Eye, Clock, RefreshCw, Loader2, Plus } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import SendManualEmailModal from "./SendManualEmailModal";

interface EmailRecord {
  id: string;
  user_id: string;
  email_type: string;
  sent_at: string;
  opened: boolean;
  user_email?: string;
}

const AdminEmailDashboard = () => {
  const [emails, setEmails] = useState<EmailRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("all");
  const [filterPeriod, setFilterPeriod] = useState("30");
  const [showManualModal, setShowManualModal] = useState(false);

  const fetchEmails = async () => {
    setLoading(true);
    try {
      const daysAgo = new Date();
      daysAgo.setDate(daysAgo.getDate() - parseInt(filterPeriod));

      let query = supabase
        .from('recovery_emails_sent')
        .select('*')
        .gte('sent_at', daysAgo.toISOString())
        .order('sent_at', { ascending: false });

      if (filterType !== 'all') {
        query = query.eq('email_type', filterType);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Fetch user emails for display
      const userIds = [...new Set((data || []).map(e => e.user_id))];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, email')
        .in('id', userIds);

      const profileMap = new Map(profiles?.map(p => [p.id, p.email]) || []);

      setEmails((data || []).map(e => ({
        ...e,
        user_email: profileMap.get(e.user_id) || 'N/A'
      })));
    } catch (err) {
      console.error('Erro ao buscar emails:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEmails(); }, [filterType, filterPeriod]);

  const stats = {
    total: emails.length,
    day2: emails.filter(e => e.email_type === 'day2').length,
    day5: emails.filter(e => e.email_type === 'day5').length,
    day10: emails.filter(e => e.email_type === 'day10').length,
    recovery: emails.filter(e => e.email_type === 'recovery').length,
    opened: emails.filter(e => e.opened).length,
  };

  const openRate = stats.total > 0 ? ((stats.opened / stats.total) * 100).toFixed(1) : '0';

  const typeLabel: Record<string, string> = {
    day2: 'Boas-vindas (2d)',
    day5: 'Oferta 20% (5d)',
    day10: 'Urgência (10d)',
    recovery: 'Recuperação (15d+)',
    manual: 'Envio Manual',
    welcome: 'Boas-vindas',
    offer20: 'Oferta 20%',
    urgency: 'Urgência',
  };

  const typeBadgeColor: Record<string, string> = {
    day2: 'bg-blue-500',
    day5: 'bg-green-500',
    day10: 'bg-orange-500',
    recovery: 'bg-purple-500',
    manual: 'bg-pink-500',
    welcome: 'bg-blue-500',
    offer20: 'bg-green-500',
    urgency: 'bg-orange-500',
  };

  // Daily chart data (last 30 days)
  const dailyData = (() => {
    const map: Record<string, number> = {};
    for (let i = 29; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const key = d.toISOString().slice(5, 10);
      map[key] = 0;
    }
    emails.forEach(e => {
      const key = e.sent_at.slice(5, 10);
      if (key in map) map[key]++;
    });
    return Object.entries(map).map(([day, count]) => ({ day, count }));
  })();

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <Send className="h-6 w-6 mx-auto mb-1 text-blue-500" />
            <p className="text-2xl font-bold">{stats.total}</p>
            <p className="text-xs text-muted-foreground">Total Enviados</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Eye className="h-6 w-6 mx-auto mb-1 text-green-500" />
            <p className="text-2xl font-bold">{openRate}%</p>
            <p className="text-xs text-muted-foreground">Taxa Abertura</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-500">{stats.day2}</p>
            <p className="text-xs text-muted-foreground">Boas-vindas</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-500">{stats.day5}</p>
            <p className="text-xs text-muted-foreground">Oferta 20%</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-orange-500">{stats.day10}</p>
            <p className="text-xs text-muted-foreground">Urgência</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-purple-500">{stats.recovery}</p>
            <p className="text-xs text-muted-foreground">Recuperação</p>
          </CardContent>
        </Card>
      </div>

      {/* Daily chart */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Envios por Dia (últimos 30 dias)</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={dailyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill="hsl(var(--primary))" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Emails Enviados
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => setShowManualModal(true)}>
                <Plus className="h-4 w-4 mr-1" /> Enviar Manual
              </Button>
              <Button variant="outline" size="sm" onClick={fetchEmails} disabled={loading}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3 mb-4 flex-wrap">
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Tipo de email" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os tipos</SelectItem>
                <SelectItem value="day2">Boas-vindas (2d)</SelectItem>
                <SelectItem value="day5">Oferta 20% (5d)</SelectItem>
                <SelectItem value="day10">Urgência (10d)</SelectItem>
                <SelectItem value="recovery">Recuperação (15d+)</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filterPeriod} onValueChange={setFilterPeriod}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Período" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Últimas 24h</SelectItem>
                <SelectItem value="7">Últimos 7 dias</SelectItem>
                <SelectItem value="30">Últimos 30 dias</SelectItem>
                <SelectItem value="90">Últimos 90 dias</SelectItem>
                <SelectItem value="365">Último ano</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : emails.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">Nenhum email encontrado no período selecionado.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Destinatário</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Data de Envio</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {emails.map(email => (
                    <TableRow key={email.id}>
                      <TableCell className="font-mono text-sm">{email.user_email}</TableCell>
                      <TableCell>
                        <Badge className={`${typeBadgeColor[email.email_type] || 'bg-gray-500'} text-white`}>
                          {typeLabel[email.email_type] || email.email_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {email.opened ? (
                          <Badge className="bg-green-500 text-white">
                            <Eye className="w-3 h-3 mr-1" /> Aberto
                          </Badge>
                        ) : (
                          <Badge variant="secondary">
                            <Clock className="w-3 h-3 mr-1" /> Enviado
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {new Date(email.sent_at).toLocaleString('pt-BR')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <SendManualEmailModal
        open={showManualModal}
        onClose={() => setShowManualModal(false)}
        onSent={fetchEmails}
      />
    </div>
  );
};

export default AdminEmailDashboard;
